import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { toast } from "react-hot-toast";
import { motion } from "framer-motion";
import { useSelector, useDispatch } from "react-redux";
import {
  Check,
  Copy,
  Edit3,
  Save,
  User,
  BarChart2,
  Send,
  Layers,
} from "lucide-react";
import { Item, RootState } from "../../redux/type";
import { setUserData } from "../../redux/actions";
import UserProfile from "../Tasks/UserProfile";
import { setHandleApi, updateWalletAddress } from "../../api/user";
import {
  SiInstagram,
  SiX,
  SiWalletconnect,
} from "@icons-pack/react-simple-icons";
import { TonConnectButton, useTonWallet } from "@tonconnect/ui-react";
import { shortenAddress } from "../../utils/helpers";
import { useWebApp } from "@vkruglikov/react-telegram-web-app";

// Design Tokens
const colors = {
  primary: "#ccff00",
  secondary: "#A5B4FC",
  accent: "#F59E0B",
  background: "#1E293B",
  surface: "#334155",
  text: "#F8FAFC",
  textSecondary: "#94A3B8",
  error: "#F87171",
  success: "#34D399",
};

const spacing = {
  xs: "0.5rem",
  sm: "0.75rem",
  md: "1rem",
  lg: "1.5rem",
  xl: "2rem",
};

const borderRadius = {
  sm: "4px",
  md: "8px",
  lg: "12px",
  xl: "16px",
};

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0% { opacity: 0.6; }
  50% { opacity: 1; }
  100% { opacity: 0.6; }
`;

// Layout Components
const Container = styled.div`
  margin: 0 auto;
  animation: ${fadeIn} 0.4s ease-out;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.25);
`;

const Content = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xl};
`;

const TabsContainer = styled.div`
  display: flex;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const TabButton = styled.button<{ $active: boolean }>`
  position: relative;
  padding: ${spacing.sm} ${spacing.md};
  background: transparent;
  border: none;
  color: ${(props) => (props.$active ? colors.primary : colors.textSecondary)};
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  transition: all 0.2s ease;

  &:after {
    content: "";
    position: absolute;
    bottom: -1px;
    left: 0;
    right: 0;
    height: 2px;
    background: ${(props) => (props.$active ? colors.primary : "transparent")};
    transition: all 0.2s ease;
  }

  &:hover {
    color: ${colors.text};
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const TabContent = styled.div`
  padding: ${spacing.md} 0;
`;

const Section = styled.section`
  padding: ${spacing.lg};
  padding-top: 0;
  margin-bottom: 4rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${spacing.lg};
`;

const SectionTitle = styled.h2`
  color: ${colors.primary};
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
`;

// Stats Components
const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: ${spacing.md};
`;

const StatCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: ${borderRadius.md};
  padding: ${spacing.md};
  border-left: 3px solid ${colors.primary};
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    background: rgba(255, 255, 255, 0.08);
  }
`;

const StatLabel = styled.div`
  font-size: 0.75rem;
  color: ${colors.textSecondary};
  margin-bottom: ${spacing.xs};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StatValue = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${colors.text};
`;

const HighlightValue = styled.span`
  color: ${colors.accent};
  font-weight: 700;
`;

// Form Components
const FormField = styled.div`
  margin-bottom: ${spacing.lg};
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 0.875rem;
  color: ${colors.textSecondary};
  margin-bottom: ${spacing.sm};
  font-weight: 500;
`;

const FieldContainer = styled.div`
  display: flex;
  gap: ${spacing.sm};
  align-items: center;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const Input = styled.input<{ $editable: boolean }>`
  flex: 1;
  padding: ${spacing.sm} ${spacing.md};
  border-radius: ${borderRadius.md};
  border: 1px solid
    ${(props) =>
      props.$editable ? colors.primary : "rgba(255, 255, 255, 0.1)"};
  background: rgba(255, 255, 255, 0.05);
  color: ${colors.text};
  font-size: 0.9375rem;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${colors.primary};
    box-shadow: 0 0 0 2px rgba(110, 231, 183, 0.2);
  }

  &:disabled {
    background: rgba(255, 255, 255, 0.03);
    color: ${colors.textSecondary};
    cursor: not-allowed;
  }
`;

const ActionButton = styled(motion.button)<{
  $variant?: "primary" | "secondary";
}>`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  padding: ${spacing.sm} ${spacing.md};
  border-radius: ${borderRadius.md};
  border: none;
  background: ${(props) =>
    props.$variant === "primary" ? colors.primary : "rgba(255, 255, 255, 0.1)"};
  color: ${(props) =>
    props.$variant === "primary" ? colors.background : colors.text};
  font-weight: 500;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: ${(props) =>
      props.$variant === "primary" ? "#50E3A4" : "rgba(255, 255, 255, 0.15)"};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &:active:not(:disabled) {
    transform: scale(0.98);
  }
`;

const IconButton = styled(motion.button)`
  background: transparent;
  border: none;
  color: ${colors.textSecondary};
  padding: ${spacing.xs};
  border-radius: ${borderRadius.sm};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: ${colors.primary};
    background: rgba(110, 231, 183, 0.1);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const LoadingIndicator = styled.div`
  animation: ${pulse} 1.5s ease-in-out infinite;
  color: ${colors.textSecondary};
`;

// Social Icons
const SocialIcon = styled.div<{ $network: string }>`
  width: 20px;
  height: 20px;
  margin-right: ${spacing.sm};
  background-color: ${(props) => {
    switch (props.$network) {
      case "twitter":
        return "#1DA1F2";
      case "instagram":
        return "#E1306C";
      case "wallet":
        return colors.accent;
      default:
        return colors.primary;
    }
  }};
  mask: ${(props) => `url(/assets/icons/${props.$network}.png)`} no-repeat
    center;
  mask-size: contain;
  display: inline-block;
  vertical-align: middle;
`;

// --- NEW STYLED COMPONENTS FOR INVENTORY ---
const InventoryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: ${spacing.md};
`;

const InventoryItem = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: ${borderRadius.md};
  padding: ${spacing.sm};
  text-align: center;
`;

const InventoryImage = styled.img`
  width: 100%;
  height: 100px;
  object-fit: cover;
  border-radius: ${borderRadius.sm};
  margin-bottom: ${spacing.sm};
`;

const InventoryName = styled.div`
  font-size: 0.8rem;
  font-weight: 500;
  color: ${colors.text};
`;

const AddToTelegramButton = styled.button`
  background: #229ed9;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 0.5rem 0.75rem;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  margin-top: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  transition: background-color 0.2s;
  &:hover {
    background: #1e87b6;
  }
`;

const EmptyState = styled.div`
  padding: ${spacing.xl};
  text-align: center;
  color: ${colors.textSecondary};
`;

// Component Props
interface InputFieldProps {
  title: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  edit: boolean;
  setEdit: (edit: boolean) => void;
  onSubmit: () => void;
  editButton?: boolean;
  loading?: boolean;
  network?: string;
  onCopy: () => void;
  copied: boolean;
}

const NetworkIcon = ({ $network }: { $network: string }) => {
  switch ($network) {
    case "twitter":
      return <SiX size={16} style={{ marginRight: spacing.xs }} />;
    case "instagram":
      return <SiInstagram size={16} style={{ marginRight: spacing.xs }} />;
    case "wallet":
      return <SiWalletconnect size={16} style={{ marginRight: spacing.xs }} />;
    default:
      return null;
  }
};

const InputField: React.FC<InputFieldProps> = ({
  title,
  value,
  onChange,
  type = "text",
  edit,
  setEdit,
  onSubmit,
  editButton = true,
  loading = false,
  network,
  onCopy,
  copied,
}) => {
  return (
    <FormField>
      <FieldLabel>
        {network && <NetworkIcon $network={network} />}
        {title}
      </FieldLabel>
      <FieldContainer>
        <Input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={`Enter your ${title.toLowerCase()}`}
          disabled={!edit}
          $editable={edit}
        />

        {/* {value && (
          <IconButton onClick={onCopy} whileTap={{ scale: 0.9 }} title="Copy to clipboard">
            {copied ? <Check size={16} /> : <Copy size={16} />}
          </IconButton>
        )} */}

        {editButton && (
          <ActionButton
            $variant={edit ? "primary" : "secondary"}
            onClick={edit ? onSubmit : () => setEdit(true)}
            disabled={edit && (!value.trim() || loading)}
            whileTap={{ scale: 0.98 }}
          >
            {loading ? (
              <LoadingIndicator>Saving...</LoadingIndicator>
            ) : edit ? (
              <>
                <Save size={16} /> Save
              </>
            ) : (
              <>
                <Edit3 size={16} /> Edit
              </>
            )}
          </ActionButton>
        )}
      </FieldContainer>
    </FormField>
  );
};

const StatisticsTab: React.FC = () => {
  const user = useSelector((state: RootState) => state.userData);

  const formatLastActive = (timestamp?: string | number): string => {
    if (!timestamp) return "Never active";

    const now = Date.now();
    const lastActive = new Date(timestamp).getTime();
    const diffInMinutes = Math.floor((now - lastActive) / (1000 * 60));

    if (diffInMinutes < 1) return "Just now";
    if (diffInMinutes < 60) return `${diffInMinutes} min ago`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours} hr ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays} day${diffInDays !== 1 ? "s" : ""} ago`;
  };

  return (
    <TabContent>
      <Section>
        <SectionHeader>
          <SectionTitle>Activity Stats</SectionTitle>
        </SectionHeader>
        <StatsGrid>
          <StatCard>
            <StatLabel>Balance</StatLabel>
            <StatValue>
              <HighlightValue>{user.points}</HighlightValue> XP
            </StatValue>
          </StatCard>

          <StatCard>
            <StatLabel>Finished Tasks</StatLabel>
            <StatValue>
              <HighlightValue>{user.completedTasks.length}</HighlightValue>{" "}
              tasks
            </StatValue>
          </StatCard>

          <StatCard>
            <StatLabel>Last Active</StatLabel>
            <StatValue>
              {formatLastActive(new Date(user.lastActivityAt)?.getTime())}
            </StatValue>
          </StatCard>

          <StatCard>
            <StatLabel>Current Streak</StatLabel>
            <StatValue>
              <HighlightValue>
                {user.lifetimeLoginDays + user.dailyStreak}
              </HighlightValue>{" "}
              days
            </StatValue>
          </StatCard>
        </StatsGrid>
      </Section>
    </TabContent>
  );
};

const SettingsTab: React.FC = () => {
  const user = useSelector((state: RootState) => state.userData);
  const dispatch = useDispatch();

  const [socialData, setSocialData] = useState({
    twitter: user.twitterHandle || "",
    instagram: user.instagramHandle || "",
    linkedIn: user.linkedInHandle || "",
    facebook: user.facebookHandle || "",
    tikTok: user.tikTokHandle || "",
    discord: user.discordHandle || "",
    wallet: user.walletAddress || "",
  });

  const [editState, setEditState] = useState({
    twitter: false,
    instagram: false,
    linkedIn: false,
    facebook: false,
    tikTok: false,
    discord: false,
    wallet: false,
  });

  const [loading, setLoading] = useState({
    twitter: false,
    instagram: false,
    linkedIn: false,
    facebook: false,
    tikTok: false,
    discord: false,
    wallet: false,
  });

  const [copiedField, setCopiedField] = useState<string | null>(null);
  const connectedWallet = useTonWallet();

  useEffect(() => {
    setSocialData({
      twitter: user.twitterHandle || "",
      instagram: user.instagramHandle || "",
      linkedIn: user.linkedInHandle || "",
      facebook: user.facebookHandle || "",
      tikTok: user.tikTokHandle || "",
      discord: user.discordHandle || "",
      wallet: user.walletAddress || "",
    });
  }, [user]);

  const copyToClipboard = (text: string, field: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text.replace("@", ""));
    setCopiedField(field);
    toast.success("Copied to clipboard!", {
      style: { background: colors.surface, color: colors.text },
    });
    setTimeout(() => setCopiedField(null), 2000);
  };

  const updateSocialHandle = async (
    field:
      | "twitter"
      | "instagram"
      | "linkedIn"
      | "facebook"
      | "tikTok"
      | "discord",
    apiCall: (uid: string, value: string, socialMedia: string) => Promise<any>
  ) => {
    setLoading((prev) => ({ ...prev, [field]: true }));
    const toastId = toast.loading(`Updating ${field} handle...`);
    try {
      const res = await apiCall(
        user.uid,
        socialData[field].replace("@", "").trim(),
        field
      );
      if (res) {
        toast.success(`${field} updated successfully!`, { id: toastId });
        dispatch(setUserData(res));
        setEditState((prev) => ({ ...prev, [field]: false }));
      }
    } catch (error: any) {
      toast.error(error.message || `Failed to update ${field}`, {
        id: toastId,
      });
    } finally {
      setLoading((prev) => ({ ...prev, [field]: false }));
    }
  };

  // Auto-update wallet if connected
  useEffect(() => {
    if (connectedWallet?.account?.address) {
      setSocialData((prev) => ({
        ...prev,
        wallet: connectedWallet.account.address,
      }));
    }
  }, [connectedWallet]);

  useEffect(() => {
    const saveWallet = async () => {
      if (
        connectedWallet?.account?.address &&
        connectedWallet.account.address !== user.walletAddress
      ) {
        setLoading((prev) => ({ ...prev, wallet: true }));
        try {
          const res = await updateWalletAddress(
            user.uid,
            connectedWallet.account.address
          );
          dispatch(setUserData(res));
        } catch {
          toast.error("Failed to update wallet address");
        } finally {
          setLoading((prev) => ({ ...prev, wallet: false }));
        }
      }
    };
    saveWallet();
  }, [connectedWallet]);

  return (
    <TabContent>
      <Section>
        <SectionHeader>
          <SectionTitle>Profile Settings</SectionTitle>
        </SectionHeader>

        {[
          { key: "twitter", title: "Twitter Handle", network: "twitter" },
          { key: "instagram", title: "Instagram Handle", network: "instagram" },
          { key: "linkedIn", title: "LinkedIn Address", network: "linkedIn" },
          { key: "facebook", title: "Facebook Profile", network: "facebook" },
          { key: "tikTok", title: "TikTok Handle", network: "tikTok" },
          { key: "discord", title: "Discord Username", network: "discord" },
        ].map(({ key, title, network }) => (
          <InputField
            key={key}
            title={title}
            value={socialData[key as keyof typeof socialData]}
            onChange={(val) =>
              setSocialData((prev) => ({ ...prev, [key]: val }))
            }
            edit={editState[key as keyof typeof editState]}
            setEdit={(edit) =>
              setEditState((prev) => ({ ...prev, [key]: edit }))
            }
            onSubmit={() =>
              updateSocialHandle(
                key as
                  | "twitter"
                  | "instagram"
                  | "linkedIn"
                  | "facebook"
                  | "tikTok"
                  | "discord",
                setHandleApi
              )
            }
            network={network}
            onCopy={() =>
              copyToClipboard(socialData[key as keyof typeof socialData], key)
            }
            copied={copiedField === key}
            loading={loading[key as keyof typeof loading]}
          />
        ))}

        <div style={{ marginBottom: "1rem" }}>
          <TonConnectButton />
        </div>

        {socialData.wallet && (
          <div
            style={{
              background: "rgba(255,255,255,0.05)",
              padding: spacing.md,
              borderRadius: borderRadius.md,
              color: colors.text,
              display: "flex",
              alignItems: "center",
              gap: spacing.sm,
              userSelect: "all",
              cursor: "pointer",
              maxWidth: "100%",
            }}
            onClick={() => copyToClipboard(socialData.wallet, "wallet")}
            title="Click to copy wallet address"
          >
            <SiWalletconnect size={18} />
            <span>{shortenAddress(socialData.wallet)}</span>
          </div>
        )}
      </Section>
    </TabContent>
  );
};

// --- NEW COMPONENT: InventoryTab ---
const InventoryTab: React.FC = () => {
  const user = useSelector((state: RootState) => state.userData);
  const WebApp = useWebApp();

  const handleAddStickerPack = (url: string) => {
    if (WebApp && url) {
      WebApp.openTelegramLink(url);
    }
  };

  return (
    <TabContent>
      <Section>
        <SectionHeader>
          <SectionTitle>Your Collection</SectionTitle>
        </SectionHeader>
        {user.inventory && user.inventory.length > 0 ? (
          <InventoryGrid>
            {user.inventory.map((item: Item) => (
              <InventoryItem key={item._id}>
                <InventoryImage src={item.image} alt={item.name} />
                <InventoryName>{item.name}</InventoryName>
                {item.stickerPackUrl && (
                  <AddToTelegramButton
                    onClick={() => handleAddStickerPack(item.stickerPackUrl)}
                  >
                    <Send size={14} /> Add to Telegram
                  </AddToTelegramButton>
                )}
              </InventoryItem>
            ))}
          </InventoryGrid>
        ) : (
          <EmptyState>
            Your inventory is empty. Visit the store to claim items!
          </EmptyState>
        )}
      </Section>
    </TabContent>
  );
};

// --- MAIN PROFILE COMPONENT ---
const Profile: React.FC = () => {
  // MODIFICATION: Add 'inventory' to the state type
  const [activeTab, setActiveTab] = useState<
    "stats" | "settings" | "inventory"
  >("stats");

  return (
    <Container>
      <Content>
        <UserProfile user={useSelector((state: RootState) => state.userData)} />

        <TabsContainer>
          <TabButton
            $active={activeTab === "stats"}
            onClick={() => setActiveTab("stats")}
          >
            <BarChart2 size={18} /> Statistics
          </TabButton>

          {/* NEW ADDITION: The Inventory tab button */}
          <TabButton
            $active={activeTab === "inventory"}
            onClick={() => setActiveTab("inventory")}
          >
            <Layers size={18} /> Inventory
          </TabButton>

          <TabButton
            $active={activeTab === "settings"}
            onClick={() => setActiveTab("settings")}
          >
            <User size={18} /> Settings
          </TabButton>
        </TabsContainer>

        {/* MODIFICATION: Conditionally render the new InventoryTab component */}
        {activeTab === "stats" && <StatisticsTab />}
        {activeTab === "inventory" && <InventoryTab />}
        {activeTab === "settings" && <SettingsTab />}
      </Content>
    </Container>
  );
};

export default Profile;
